/**
 * Nepali locale for extensions-ui.
 * Export is the "extensions" subtree: host apps merge this into their i18n
 * resources as translation.extensions so that t('extensions.list.heading') etc. resolve.
 */
import type { ExtensionsLocale } from './en'

export const extensionsNe = {
  // ---------------------------------------------------------------------------
  // Common / shared labels
  // ---------------------------------------------------------------------------
  common: {
    cancel: `रद्द गर्नुहोस्`,
    delete: `मेटाउनुहोस्`,
    save: `सेभ गर्नुहोस्`,
    apply: `लागू गर्नुहोस्`,
    edit: `सम्पादन गर्नुहोस्`,
    actions: `कार्यहरू`,
    add: `थप्नुहोस्`,
    all: `सबै`,
    loading: `लोड हुँदैछ...`,
    noChange: `परिवर्तन छैन`,
    none: `कुनै पनि छैन`,
  },

  // ---------------------------------------------------------------------------
  // List page
  // ---------------------------------------------------------------------------
  list: {
    pageLabel: `सबै एक्सटेन्सनहरू`,
    heading: `एक्सटेन्सनहरू`,
    linesHeading: `लाइनहरू`,
    searchPlaceholder: `नाम वा नम्बरले खोज्नुहोस्`,
    linesSearchPlaceholder: `नाम, नम्बर, तोकिएको प्रोफाइल वा स्थानले खोज्नुहोस्`,
    loadingExtensions: `एक्सटेन्सनहरू लोड हुँदैछ...`,
    noMatch: `तपाईंको खोजसँग मिल्ने कुनै एक्सटेन्सन छैन`,
    noExtensions: `कुनै एक्सटेन्सन भेटिएन`,
    noMatchHint: `फरक खोज शब्द प्रयास गर्नुहोस्।`,
    noExtensionsHint: `यो पीबीएक्सको लागि कुनै एक्सटेन्सन छैन।`,
    activateAll: `सबै सक्रिय गर्नुहोस्`,
    deactivateAll: `सबै निष्क्रिय गर्नुहोस्`,
    massEdit: `सामूहिक सम्पादन`,
    linesSelected: `{{count}} लाइन चयन गरिएको`,
  },

  // ---------------------------------------------------------------------------
  // Column headers
  // ---------------------------------------------------------------------------
  columns: {
    number: `नम्बर`,
    name: `नाम`,
    type: `प्रकार`,
    created: `सिर्जना गरिएको`,
    updated: `अद्यावधिक गरिएको`,
    lastUpdated: `अन्तिम अद्यावधिक`,
    assignedUser: `तोकिएको प्रयोगकर्ता`,
    assignedProfile: `तोकिएको प्रोफाइल`,
    location: `स्थान`,
    actions: `कार्यहरू`,
  },

  // ---------------------------------------------------------------------------
  // Row actions & badges
  // ---------------------------------------------------------------------------
  row: {
    inactive: `निष्क्रिय`,
    edit: `सम्पादन`,
    delete: `मेटाउनुहोस्`,
    viewCallMetrics: `कल मेट्रिक्स हेर्नुहोस्`,
    activate: `सक्रिय गर्नुहोस्`,
    deactivate: `निष्क्रिय गर्नुहोस्`,
    extensionFallback: `एक्सटेन्सन {{id}}`,
    selectAllLines: `सबै लाइनहरू चयन गर्नुहोस्`,
    selectLine: `{{name}} चयन गर्नुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Delete dialog
  // ---------------------------------------------------------------------------
  deleteDialog: {
    title: `एक्सटेन्सन मेटाउनुहोस्`,
    confirmation: `के तपाईं "{{displayName}}" मेटाउन निश्चित हुनुहुन्छ? यो कार्य पूर्ववत गर्न सकिँदैन।`,
    unnamed: `नाम नभएको एक्सटेन्सन`,
    reassignLabel: `सन्दर्भहरू पुन: तोक्नुहोस्`,
    deleteWithoutReassigning: `पुन: तोक्न बिना मेटाउनुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Set active state dialog (single)
  // ---------------------------------------------------------------------------
  activeStateDialog: {
    activateTitle: `एक्सटेन्सन सक्रिय गर्नुहोस्`,
    deactivateTitle: `एक्सटेन्सन निष्क्रिय गर्नुहोस्`,
    body: `तपाईं {{displayName}} {{action}} गर्न लाग्नुभएको छ, जसले यो एक्सटेन्सनलाई प्राथमिक लाइन एक्सटेन्सनको रूपमा प्रयोग गर्ने सबै उपकरणहरू पुन: सुरु गर्नेछ।`,
    unnamedLine: `नाम नभएको लाइन`,
    confirmPrompt: `के तपाईं जारी राख्न चाहनुहुन्छ?`,
    setActive: `सक्रिय सेट गर्नुहोस्`,
    setInactive: `निष्क्रिय सेट गर्नुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Bulk active state dialog
  // ---------------------------------------------------------------------------
  bulkActiveDialog: {
    activateTitle: `सबै एक्सटेन्सनहरू सक्रिय गर्नुहोस्`,
    deactivateTitle: `सबै एक्सटेन्सनहरू निष्क्रिय गर्नुहोस्`,
    body: `तपाईं सबै एक्सटेन्सनहरू {{action}} गर्न लाग्नुभएको छ, जसले यी एक्सटेन्सनहरूलाई प्राथमिक लाइन एक्सटेन्सनको रूपमा प्रयोग गर्ने सबै उपकरणहरू पुन: सुरु गर्नेछ।`,
    confirmPrompt: `के तपाईं जारी राख्न चाहनुहुन्छ?`,
    activateAll: `सबै सक्रिय गर्नुहोस्`,
    deactivateAll: `सबै निष्क्रिय गर्नुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Mass edit dialog
  // ---------------------------------------------------------------------------
  massEdit: {
    title: `लाइनहरूको सामूहिक सम्पादन`,
    description: `{{count}} लाइनको लागि निम्न क्षेत्रहरू अद्यावधिक गर्नुहोस्। हालको मान राख्न "परिवर्तन छैन" मा छोड्नुहोस्।`,
    outboundCallerId: `बाहिरी कलर ID`,
    localPresence: `स्थानीय उपस्थिति`,
    preferredCodec: `मनपर्ने कोडेक`,
    location: `स्थान`,
    locationPlaceholder: `परिवर्तन छैन वा खोज्न टाइप गर्नुहोस्`,
    locationSearchLabel: `स्थान खोज`,
    loadingLocations: `स्थानहरू लोड हुँदैछ\u2026`,
    noLocationsFound: `कुनै स्थान भेटिएन`,
    typeToSearchLocations: `स्थानहरू खोज्न टाइप गर्नुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Pagination / toolbar
  // ---------------------------------------------------------------------------
  pagination: {
    resultsPerPage: `पृष्ठमा नतिजा:`,
    previousPage: `अघिल्लो पृष्ठ`,
    nextPage: `अर्को पृष्ठ`,
    all: `सबै`,
    jumpToPage: `पृष्ठ {{page}} मा जानुहोस्`,
    searchExtensions: `एक्सटेन्सनहरू खोज्नुहोस्`,
    toggleFilters: `फिल्टरहरू टगल गर्नुहोस्`,
    showingAll: `सबै {{total}} नतिजा देखाइँदै`,
    showingRange: `{{start}} देखि {{end}} सम्म, जम्मा {{total}} नतिजा`,
  },

  // ---------------------------------------------------------------------------
  // Toast messages
  // ---------------------------------------------------------------------------
  toast: {
    deleteSuccess: `एक्सटेन्सन सफलतापूर्वक मेटाइयो।`,
    deleteFailed: `मेट्न असफल`,
    deleteFailedDescription: `एक्सटेन्सन मेट्न असफल भयो। कृपया फेरि प्रयास गर्नुहोस्।`,
    success: `सफल`,
    failed: `असफल`,
    activeStateSuccess: `लाइन {{state}} मा सेट गरियो।`,
    activeStateFailed: `लाइनको सक्रिय अवस्था अद्यावधिक गर्न असफल भयो।`,
    bulkSuccess: `{{count}} लाइन {{state}} मा सेट गरियो।`,
    partialFailure: `आंशिक असफलता`,
    partialFailureDescription: `{{succeeded}} सफल, {{failed}} असफल। केही लाइनहरू अद्यावधिक नभएको हुन सक्छ।`,
    massEditSuccess: `{{count}} लाइन अद्यावधिक गरियो।`,
    massEditFailed: `सामूहिक सम्पादन असफल`,
    massEditFailedDescription: `चयन गरिएका लाइनहरू अद्यावधिक गर्न असफल भयो। फेरि प्रयास गर्नुहोस्।`,
  },

  // ---------------------------------------------------------------------------
  // Extension type labels
  // ---------------------------------------------------------------------------
  types: {
    line: `लाइन`,
    ringGroup: `रिङ समूह`,
    virtualFax: `भर्चुअल फ्याक्स मेसिन`,
    conferenceRoom: `सम्मेलन कोठा`,
    switchboard: `स्विचबोर्ड`,
    sharedVoicemail: `साझा भ्वाइसमेल बक्स`,
    callQueue: `कल लाम`,
  },

  // ---------------------------------------------------------------------------
  // View mode labels
  // ---------------------------------------------------------------------------
  viewModes: {
    extensions: `एक्सटेन्सनहरू`,
    lines: `लाइनहरू`,
    ringGroups: `रिङ समूहहरू`,
    callQueues: `कल लामहरू`,
    conferenceRooms: `सम्मेलन कोठाहरू`,
    virtualFax: `भर्चुअल फ्याक्स`,
    sharedVoicemail: `साझा भ्वाइसमेल`,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    title: `फिल्टर`,
    status: `स्थिति:`,
    assignedUser: `तोकिएको प्रयोगकर्ता:`,
    assignedProfile: `तोकिएको प्रोफाइल:`,
    location: `स्थान:`,
    active: `सक्रिय`,
    inactive: `निष्क्रिय`,
    all: `सबै`,
    clearFilters: `फिल्टरहरू खाली गर्नुहोस्`,
    apply: `लागू गर्नुहोस्`,
  },

  placeholder: {
    notImplemented: `{{typeLabel}} सम्पादन पृष्ठ अझै कार्यान्वयन गरिएको छैन।`,
    backToList: `{{listLabel}} मा फर्कनुहोस्`,
  },

  edit: {
    validationError: `मान्यता त्रुटि`,
    reviewRequiredFields: `कृपया आवश्यक क्षेत्रहरू जाँच गरेर फेरि प्रयास गर्नुहोस्।`,
    emailFormatInvalid: `इमेल ढाँचा गलत छ। खाली ठाउँ नभएको सुनिश्चित गर्नुहोस्। धेरै ठेगानाहरूलाई अल्पविरामले छुट्याउनुहोस्।`,
    nameNumberRequired: `नाम र नम्बर आवश्यक छ।`,
    reservedNumber: `{{number}} आरक्षित छ। कृपया फरक नम्बर चयन गर्नुहोस्।`,
    createFailed: `सिर्जना असफल`,
    updateFailed: `अद्यावधिक असफल`,
    breadcrumbAdd: `{{typeLabel}} थप्नुहोस्`,
    breadcrumbEdit: `{{typeLabel}} {{value}} सम्पादन गर्नुहोस्`,
    backToList: `← {{listLabel}} मा फर्कनुहोस्`,
    wrongPbx: `यो एक्सटेन्सन चयन गरिएको पीबीएक्समा छैन`,
    wrongPbxDescription: `यो एक्सटेन्सन चयन गरिएको पीबीएक्ससँग सम्बन्धित छैन।`,
    openInPbx: `{{pbxName}} मा खोल्नुहोस्`,
    loadFailed: `यो एक्सटेन्सन लोड गर्न सकिएन`,
    options: `विकल्पहरू`,
    saveChanges: `परिवर्तनहरू सेभ गर्नुहोस्`,
    omitFromDirectory: `डाइरेक्टरीबाट हटाउनुहोस्`,
    nameRequired: `नाम आवश्यक छ`,
    numberRequired: `नम्बर आवश्यक छ`,
    extensionNumberPlaceholder: `एक्सटेन्सन नम्बर`,
    yes: `हो`,
    no: `होइन`,
    members: `सदस्यहरू`,
    membersNoun: `सदस्यहरू`,
    userGroups: `प्रयोगकर्ता समूहहरू`,
    userGroupsNoun: `प्रयोगकर्ता समूहहरू`,
    holdMusic: `होल्ड म्युजिक`,
    line: {
      requiredFields: `नाम, नम्बर, स्थान र तोकिएको प्रोफाइल आवश्यक छ।`,
      selectLocation: `स्थान छान्नुहोस्`,
      notType: `लाइन होइन`,
      pbxUuid: `पीबीएक्स UUID`,
      pbxName: `पीबीएक्स`,
      sipSecret: `SIP सेक्रेट`,
      sipSecretReset: `SIP सेक्रेट रिसेट गरियो`,
      reset: `रिसेट गर्नुहोस्`,
      extensionUuid: `एक्सटेन्सन UUID`,
      dialNow: `डायल नाउ`,
      dialNowCleared: `डायल नाउ खाली गरियो`,
      clear: `खाली गर्नुहोस्`,
      assignedProfileMobileDevices: `तोकिएको प्रोफाइलका मोबाइल उपकरणहरू`,
      addTitle: `लाइन थप्नुहोस्`,
      title: `लाइन`,
      voicemailOptions: `भ्वाइसमेल`,
      outDialExtension: `OutDial एक्सटेन्सन`,
      outDialHelp: `OutDial एक्सटेन्सन सहायता`,
      outDialHelpNo: `होइन: यो एक्सटेन्सनले नियमित एक्सटेन्सनको रूपमा काम गर्छ।`,
      outDialHelpYes: `हो: यो एक्सटेन्सनले केवल फर्वार्डिङ नम्बरमा मात्र फर्वार्ड गर्छ।`,
      outDialHelpBeAware: `ध्यान दिनुहोस्:`,
      outDialHelpWarningOne: `कल बाहिरी नम्बरमा फर्वार्ड भइसकेपछि प्रणालीले त्यसलाई फिर्ता लिन सक्दैन।`,
      outDialHelpWarningTwo: `OutDial एक्सटेन्सनसँग प्रणाली भ्वाइसमेल प्रयोग गर्न सकिँदैन।`,
      outDialEnabledHint: `OutDial सक्षम छ। कलहरू फर्वार्डिङ नम्बरमा फर्वार्ड हुन्छन्; बाहिरी कलर ID अझै बाहिरी कल खण्डमा लागू हुन्छ।`,
      outDialDisabledHint: `OutDial असक्षम छ। बाहिरी कलर सेटिङहरू लागू हुन्छन्।`,
      loadingProfiles: `प्रोफाइलहरू लोड हुँदैछ…`,
      voicemailEnabled: `भ्वाइसमेल सक्षम`,
      forwardingNumber: `फर्वार्डिङ नम्बर (OutDial)`,
      localAreaCode: `स्थानीय एरिया कोड`,
      localAreaCodeInvalid: `एरिया कोड अमान्य छ। स्थानीय एरिया कोडको लागि {{areaCode}} अनुमति छैन।`,
      localAreaCodeNotAllowed: `एरिया कोड {{areaCode}} स्थानीय एरिया कोडको लागि अनुमति छैन।`,
      outboundCallerIdRequired: `कृपया बाहिरी कलर ID चयन गर्नुहोस्।`,
      outboundCallerIdNoOptions: `यो पीबीएक्समा कुनै फोन नम्बर कन्फिगर गरिएको छैन। यस लाइनलाई सम्पादन गर्नु अघि पीबीएक्समा फोन नम्बर थप्नुहोस्।`,
      outboundCallerIdMissingSavedValue: `यस लाइनमा मान्य बाहिरी कलर ID छैन। जारी राख्नु अघि एउटा चयन गरेर सुरक्षित गर्नुहोस्।`,
      expandDetails: `विवरण देखाउनुहोस्`,
      collapseDetails: `विवरण लुकाउनुहोस्`,
      activate: `सक्रिय गर्नुहोस्`,
      deactivate: `निष्क्रिय गर्नुहोस्`,
      findMe: {
        title: `मलाई फेला पार्नुहोस्`,
        mode: `मलाई फेला पार्नुहोस्`,
        number: `फेला पार्ने नम्बर`,
        delay: `यसपछि फेला पार्नुहोस्`,
        modeOptions: {
          '0': `बन्द`,
          '1': `खुला`,
          '2': `सबै एकै साथ बजाउनुहोस्`,
        },
        delayOptions: {
          '0': `तुरुन्तै`,
          '5': `५ सेकेन्ड`,
          '10': `१० सेकेन्ड`,
          '15': `१५ सेकेन्ड`,
        },
        numberRequired: `मलाई फेला पार्नुहोस् खुला हुँदा फेला पार्ने नम्बर अनिवार्य छ।`,
        numberInvalid: `फेला पार्ने नम्बरमा अंकहरू र सुरुमा वैकल्पिक + मात्र हुन सक्छ (अधिकतम १५ अंक)।`,
      },
      callRecording: `कल रेकर्डिङ`,
      callRecordingOptions: {
        '0': `होइन`,
        '1': `हो`,
        '2': `प्रणालीद्वारा परिभाषित`,
      },
      dedicatedFax: `समर्पित फ्याक्स`,
      inCallFeatureCodes: `इन-कल फिचर कोडहरू सक्षम`,
      createButton: `लाइन सिर्जना गर्नुहोस्`,
      saveAndCreateAnother: `सुरक्षित गर्नुहोस् र अर्को बनाउनुहोस्`,
      details: `लाइन विवरण`,
      codecOpus: `ढिलो इन्टरनेटका लागि उत्तम (OPUS) समर्थन: वेब फोनहरू, Yealink`,
      codecPcmu: `मानक (PCMU) समर्थन: सबै प्रकारका फोनहरू`,
      codecG722: `HD अडियो (G722) समर्थन: Yealink, Cisco`,
      codecG729: `असमर्थित (DEPRECATED) - ढिलो इन्टरनेटका लागि राम्रो (G729) समर्थन: Yealink, Cisco`,
    },
    ringGroup: {
      ringing: `घण्टी बज्दैछ`,
      created: `रिङ समूह सिर्जना गरियो`,
      updated: `रिङ समूह अद्यावधिक गरियो`,
      saveFailed: `रिङ समूह सेभ गर्न सकिएन`,
      notType: `रिङ समूह होइन`,
      notTypeDescription: `यो एक्सटेन्सन रिङ समूह होइन। अन्य प्रकारहरू सम्पादन गर्न एक्सटेन्सन सूची प्रयोग गर्नुहोस्।`,
      namePlaceholder: `रिङ समूहको नाम`,
      ringStrategy: `रिङ रणनीति`,
      ringAll: `सबैलाई घण्टी`,
      pagingGroup: `पेजिङ समूह`,
      ringbackAudio: `रिङब्याक अडियो`,
      createButton: `रिङ समूह सिर्जना गर्नुहोस्`,
      updateButton: `रिङ समूह अद्यावधिक गर्नुहोस्`,
    },
    callQueue: {
      created: `कल लाम सिर्जना गरियो`,
      updated: `कल लाम अद्यावधिक गरियो`,
      saveFailed: `कल लाम सेभ गर्न सकिएन`,
      notType: `कल लाम होइन`,
      queueSettings: `लाम सेटिङहरू`,
      ringStrategy: `रिङ रणनीति`,
      strategyRingAll: `सबैलाई घण्टी`,
      strategyRoundRobin: `राउन्ड रोबिन`,
      strategyLongestIdle: `सबैभन्दा लामो निष्क्रिय`,
      strategyRandom: `अनियमित`,
      strategyLeastTalkTime: `सबैभन्दा कम कुराकानी समय`,
      strategyFewestCalls: `सबैभन्दा थोरै कलहरू`,
      preCallAnnouncement: `कल अघिको घोषणा`,
      maxNoAnswer: `अधिकतम उत्तर नदिएको (प्रयासहरू)`,
      busyDelayTime: `व्यस्त ढिलाइ समय (सेकेन्ड)`,
      rejectDelayTime: `अस्वीकार ढिलाइ समय (सेकेन्ड)`,
      wrapUpTime: `समापन समय (सेकेन्ड)`,
      noAnswerDelayTime: `उत्तर नदिएको ढिलाइ समय (सेकेन्ड)`,
      maxRingDuration: `अधिकतम घण्टी अवधि (सेकेन्ड)`,
      maxCallersInQueue: `लाममा अधिकतम कल गर्नेहरू`,
      maxWaitTime: `अधिकतम प्रतीक्षा समय (सेकेन्ड)`,
      timeoutDestination: `टाइमआउट गन्तव्य`,
      escapeDestination: `निकास गन्तव्य`,
      destinationSearchPlaceholder: `एक्सटेन्सनहरू खोज्नुहोस्...`,
      destinationNoMatches: `त्यो खोजसँग मिल्ने कुनै एक्सटेन्सन छैन।`,
      destinationCurrentUnlisted: `{{value}} (हालको)`,
      periodicAnnouncements: `आवधिक घोषणाहरू`,
      positionAnnouncementFrequency: `स्थिति घोषणा आवृत्ति`,
      positionAnnouncementMessage: `स्थिति घोषणा सन्देश`,
      queuePosition: `लाम स्थिति`,
      agentsNoun: `एजेन्टहरू`,
      newButton: `नयाँ कल लाम`,
      updateButton: `कल लाम अद्यावधिक गर्नुहोस्`,
    },
    conferenceRoom: {
      moderatorPinValidation: `मोडरेटर PIN ठ्याक्कै 4 अंकको हुनुपर्छ।`,
      conferencePinValidation: `सम्मेलन PIN ठ्याक्कै 4 अंकको हुनुपर्छ।`,
      holdMusicRequired: `होल्ड म्युजिक आवश्यक छ।`,
      created: `सम्मेलन कोठा सिर्जना गरियो`,
      updated: `सम्मेलन कोठा अद्यावधिक गरियो`,
      saveFailed: `सम्मेलन कोठा सेभ गर्न सकिएन`,
      notType: `सम्मेलन कोठा होइन`,
      moderatorRequired: `मोडरेटर आवश्यक`,
      moderatorPin: `मोडरेटर PIN`,
      userPinRequired: `प्रयोगकर्ता PIN आवश्यक`,
      conferencePin: `सम्मेलन PIN`,
      defaultMuted: `पूर्वनिर्धारित रूपमा म्युट`,
      announceNewParticipants: `नयाँ सहभागीहरूको घोषणा गर्नुहोस्`,
      conferenceGreeting: `सम्मेलन अभिवादन`,
      selectHoldMusic: `होल्ड म्युजिक चयन गर्नुहोस्`,
      createButton: `सम्मेलन कोठा सिर्जना गर्नुहोस्`,
      updateButton: `सम्मेलन कोठा अद्यावधिक गर्नुहोस्`,
    },
    virtualFax: {
      invalidEmail: `कृपया मान्य इमेल ठेगाना प्रविष्ट गर्नुहोस्।`,
      passwordsDoNotMatch: `पासवर्डहरू मिल्दैनन्।`,
      passwordMinLength: `सुरक्षित फ्याक्स पासवर्ड कम्तीमा 8 वर्णको हुनुपर्छ।`,
      created: `भर्चुअल फ्याक्स सिर्जना गरियो`,
      updated: `भर्चुअल फ्याक्स अद्यावधिक गरियो`,
      saveFailed: `भर्चुअल फ्याक्स सेभ गर्न सकिएन`,
      secureFaxLinkResent: `सुरक्षित फ्याक्स लिङ्क पुनः पठाइयो`,
      resendFailed: `पुनः पठाउन असफल`,
      resendSaveFailed: `सुरक्षित फ्याक्स लिङ्क पुनः पठाउन सकिएन`,
      notType: `भर्चुअल फ्याक्स होइन`,
      receivingOptions: `प्राप्ति विकल्पहरू`,
      receivingEmailDestination: `प्राप्त गर्ने इमेल गन्तव्य`,
      secureFax: `सुरक्षित फ्याक्स`,
      download: `डाउनलोड`,
      singleDownload: `एकल डाउनलोड`,
      download5: `5 डाउनलोड`,
      download10: `10 डाउनलोड`,
      download15: `15 डाउनलोड`,
      failedLoginRetries: `गलत पासवर्ड प्रयास सीमा`,
      retries5: `5 पुनःप्रयास`,
      retries10: `10 पुनःप्रयास`,
      retries15: `15 पुनःप्रयास`,
      linkExpiresIn: `डाउनलोड लिङ्कको म्याद सकिने अवधि`,
      day1: `1 दिन`,
      day2: `2 दिन`,
      day3: `3 दिन`,
      day7: `7 दिन`,
      day15: `15 दिन`,
      day30: `30 दिन`,
      changePasswordOptional: `सुरक्षित फ्याक्स पासवर्ड परिवर्तन गर्नुहोस् (वैकल्पिक)`,
      userGroupsDescription: `यो भर्चुअल फ्याक्समा कुन प्रयोगकर्ता समूहहरूको पहुँच हुनुपर्छ छान्नुहोस्।`,
      sending: `पठाउँदै…`,
      resendSecureFaxLink: `सुरक्षित फ्याक्स लिङ्क पुनः पठाउनुहोस्`,
      createButton: `भर्चुअल फ्याक्स मेसिन सिर्जना गर्नुहोस्`,
      updateButton: `भर्चुअल फ्याक्स मेसिन अद्यावधिक गर्नुहोस्`,
    },
    shared: {
      generalOptions: `सामान्य विकल्पहरू`,
      voicemailTimeZone: `भ्वाइसमेल समय क्षेत्र`,
      voicemailPassword: `भ्वाइसमेल पासवर्ड`,
      playVoicemailInstructions: `भ्वाइसमेल निर्देशनहरू बजाउनुहोस्`,
      notificationOptions: `सूचना विकल्पहरू`,
      sendEmailNotifications: `इमेल सूचनाहरू पठाउनुहोस्`,
      messageEmailAddress: `सन्देश इमेल ठेगाना`,
      emailPlaceholder: `email@example.com`,
      attachVoicemailMessage: `भ्वाइसमेल सन्देश संलग्न गर्नुहोस्`,
      deleteMessageAfterSend: `पठाएपछि सन्देश मेटाउनुहोस्`,
      outgoingMessage: `बाहिर जाने सन्देश`,
      useSystemGreetings: `प्रणाली अभिवादनहरू प्रयोग गर्नुहोस्`,
      customGreeting: `कस्टम अभिवादन`,
    },
    sharedVoicemail: {
      created: `साझा भ्वाइसमेल सिर्जना गरियो`,
      updated: `साझा भ्वाइसमेल अद्यावधिक गरियो`,
      saveFailed: `साझा भ्वाइसमेल सेभ गर्न सकिएन`,
      notType: `साझा भ्वाइसमेल होइन`,
      userGroupsDescription: `यो साझा भ्वाइसमेल बक्समा कुन प्रयोगकर्ता समूहहरूको पहुँच हुनुपर्छ छान्नुहोस्।`,
      createButton: `साझा भ्वाइसमेल सिर्जना गर्नुहोस्`,
      updateButton: `साझा भ्वाइसमेल अद्यावधिक गर्नुहोस्`,
    },
  },

  updateLocation: {
    title: `स्थान अद्यावधिक गर्नुहोस्`,
    description: `{{count}} लाइनको लागि स्थान अद्यावधिक गर्ने हो?`,
    confirm: `स्थान अद्यावधिक गर्नुहोस्`,
  },

  pickList: {
    assignedWithLimit: `तोकिएका {{itemNoun}}: {{count}} / {{max}}`,
    assigned: `तोकिएका {{itemNoun}}: {{count}}`,
    noneAssigned: `हालसम्म कुनै {{itemNoun}} थपिएको छैन।`,
    remove: `हटाउनुहोस्`,
    available: `उपलब्ध {{itemNoun}}: {{count}}`,
    searchAvailable: `उपलब्ध {{itemNoun}} खोज्नुहोस्`,
    maxReached: `अधिकतम {{max}} {{itemNoun}} को सीमा पुग्यो।`,
    noneAvailable: `कुनै {{itemNoun}} उपलब्ध छैन।`,
  },

  localPresence: {
    0: `बन्द`,
    1: `समूह 1`,
    2: `समूह 2`,
    3: `समूह 3`,
    4: `समूह 4`,
    5: `समूह 5`,
    6: `समूह 6`,
  },

  codecOptions: {
    opus: `ढिलो इन्टरनेटका लागि उत्तम (OPUS) — समर्थन: वेब फोनहरू, Yealink`,
    pcmu: `मानक (PCMU) — समर्थन: सबै प्रकारका फोनहरू`,
    g722: `HD अडियो (G722) — समर्थन: Yealink, Cisco`,
    g729: `असमर्थित (DEPRECATED) — ढिलो इन्टरनेटका लागि राम्रो (G729) — समर्थन: Yealink, Cisco`,
    default: `प्रणाली पूर्वनिर्धारित`,
  },

  timezones: {
    america_new_york: `Eastern Time`,
    america_chicago: `Central Time`,
    america_denver: `Mountain Time`,
    america_los_angeles: `Pacific Time`,
    america_phoenix: `Arizona`,
    america_anchorage: `Alaska Time`,
    pacific_honolulu: `Hawaii Time`,
    america_toronto: `Toronto (Eastern)`,
    america_vancouver: `प्रशान्त समय - ब्रिटिश कोलम्बिया (PCT)`,
    europe_london: `London (GMT/BST)`,
    europe_paris: `Paris (CET)`,
    australia_sydney: `Sydney (AEST)`,
    utc: `UTC`,
  },

  // ---------------------------------------------------------------------------
  // Create extension options
  // ---------------------------------------------------------------------------
  createOptions: {
    add: `थप्नुहोस्`,
    addLine: `लाइन थप्नुहोस्`,
    addRingGroup: `रिङ समूह थप्नुहोस्`,
    addCallQueue: `कल लाम थप्नुहोस्`,
    addConferenceRoom: `सम्मेलन कोठा थप्नुहोस्`,
    addVirtualFax: `भर्चुअल फ्याक्स मेसिन थप्नुहोस्`,
    addSharedVoicemail: `साझा भ्वाइसमेल बक्स थप्नुहोस्`,
  },

  // ---------------------------------------------------------------------------
  // Ordering options
  // ---------------------------------------------------------------------------
  ordering: {
    lastUpdatedNewest: `अन्तिम अद्यावधिक (नयाँ पहिले)`,
    lastUpdatedOldest: `अन्तिम अद्यावधिक (पुरानो पहिले)`,
    createdNewest: `सिर्जना गरिएको (नयाँ पहिले)`,
    createdOldest: `सिर्जना गरिएको (पुरानो पहिले)`,
    nameAZ: `नाम (A\u2013Z)`,
    nameZA: `नाम (Z\u2013A)`,
    numberAsc: `नम्बर (बढ्दो)`,
    numberDesc: `नम्बर (घट्दो)`,
  },

  // ---------------------------------------------------------------------------
  // Slug-to-label mappings (for breadcrumbs)
  // ---------------------------------------------------------------------------
  slugLabels: {
    line: `लाइन`,
    ringGroup: `रिङ समूह`,
    callQueue: `कल लाम`,
    conferenceRoom: `सम्मेलन कोठा`,
    virtualFax: `भर्चुअल फ्याक्स मेसिन`,
    sharedVoicemail: `साझा भ्वाइसमेल बक्स`,
  },

  // ---------------------------------------------------------------------------
  // List labels for type slugs
  // ---------------------------------------------------------------------------
  listLabels: {
    lines: `लाइनहरू`,
    ringGroups: `रिङ समूहहरू`,
    callQueues: `कल लामहरू`,
    conferenceRooms: `सम्मेलन कोठाहरू`,
    virtualFax: `भर्चुअल फ्याक्स`,
    sharedVoicemail: `साझा भ्वाइसमेल`,
  },
} satisfies ExtensionsLocale
